/**
 * Client crash/error telemetry — the capture half of the diagnostics pipeline.
 *
 * COPPA / GDPR-K: this reporter sends ONLY aggregate, diagnostic data and NEVER
 * child PII. It does not read the child's name, email, answers, or any free text
 * they typed. The only free-ish text it forwards is a sanitized error name/message
 * and the top stack frames (our code), all PII-scrubbed + length-capped here BEFORE
 * they leave the device. The server (support-api /diagnostics/event) re-sanitizes
 * everything again (defense in depth).
 *
 * What is catchable from JS (and IS reported here):
 *   • React render crashes  → ErrorBoundary.componentDidCatch → reportReactError()
 *   • uncaught errors        → window.onerror
 *   • unhandled promise rejections → window.unhandledrejection
 * What is NOT catchable from JS (documented, needs a native crash reporter):
 *   • a native WKWebView/Android NSException/SIGABRT that kills the process. The mic
 *     crash we fixed, IF it surfaced as a JS error/rejection, IS caught above; if it
 *     killed the native process outright it is invisible to JS and only a native
 *     crash reporter (e.g. Crashlytics) would see it.
 */

import { APP_VERSION } from '@learnwithai/ui-shared';

export type DiagnosticPlatform = 'web' | 'ios' | 'android';
export type DiagnosticEventType = 'crash' | 'error';

export interface DiagnosticEvent {
  eventType: DiagnosticEventType;
  platform: DiagnosticPlatform;
  appVersion: string;
  route: string;
  errorName: string;
  errorMessage: string;
  stackTop: string;
  sessionId: string;
  childAgeBand?: string;
  timestampMs: number;
}

const MAX_NAME_LEN = 120;
const MAX_MESSAGE_LEN = 300;
const MAX_STACK_FRAMES = 5;
const MAX_STACK_LEN = 1000;

// Client-side rate limiting so a render loop or a flaky network can't spam the
// ingest endpoint. At most MAX_PER_WINDOW sends per WINDOW_MS, and identical
// signatures are de-duplicated within DEDUPE_MS.
const WINDOW_MS = 30_000;
const MAX_PER_WINDOW = 5;
const DEDUPE_MS = 10_000;

let sentTimestamps: number[] = [];
const recentSignatures = new Map<string, number>();
let globalHandlersInstalled = false;

const ALLOWED_AGE_BANDS = new Set(['5-7', '8-10', '11-13']);

function now(): number {
  return typeof Date !== 'undefined' ? Date.now() : 0;
}

/** True only inside the native Capacitor shell. */
function detectPlatform(): DiagnosticPlatform {
  try {
    const cap = (globalThis as unknown as { Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string } }).Capacitor;
    if (cap?.isNativePlatform?.()) {
      const p = cap.getPlatform?.();
      if (p === 'ios' || p === 'android') return p;
    }
  } catch { /* ignore */ }
  return 'web';
}

/**
 * Where to POST. On web the relative '/api' hits the gateway. On the native shell
 * a relative '/api' never reaches the backend (origin is capacitor://localhost), so
 * use the configured absolute base — same resolution as api.ts / mobile native.ts.
 */
function ingestUrl(): string {
  const path = '/diagnostics/event';
  try {
    const cap = (globalThis as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    if (cap?.isNativePlatform?.()) {
      const configured = (import.meta as unknown as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE;
      const base = configured ? configured.replace(/\/$/, '') : 'https://aireadinglabs.com/api';
      return base + path;
    }
  } catch { /* ignore */ }
  return '/api' + path;
}

/** Path only — never query/hash (they can carry ids/PII). */
export function currentRoute(): string {
  try {
    const loc = (globalThis as unknown as { location?: { pathname?: string } }).location;
    const p = loc?.pathname || '/';
    return p.length > 0 ? p : '/';
  } catch {
    return '/';
  }
}

/**
 * Coarse age BAND only (never an exact age or name). Read from the active-child
 * blob if — and only if — it exposes a band or a numeric age; otherwise omit.
 */
export function currentAgeBand(): string | undefined {
  try {
    const store = (globalThis as unknown as { localStorage?: { getItem(k: string): string | null } }).localStorage;
    const raw = store?.getItem('learnwithai_active_child');
    if (!raw) return undefined;
    const child = JSON.parse(raw) as { ageBand?: string; age?: number };
    if (typeof child.ageBand === 'string' && ALLOWED_AGE_BANDS.has(child.ageBand)) return child.ageBand;
    if (typeof child.age === 'number' && child.age > 0) {
      if (child.age <= 7) return '5-7';
      if (child.age <= 10) return '8-10';
      return '11-13';
    }
  } catch { /* ignore */ }
  return undefined;
}

/** Opaque, non-identifying session id, persisted for the browser session only. */
export function sessionId(): string {
  try {
    const store = (globalThis as unknown as { sessionStorage?: { getItem(k: string): string | null; setItem(k: string, v: string): void } }).sessionStorage;
    const KEY = 'lwa_diag_session';
    const existing = store?.getItem(KEY);
    if (existing) return existing;
    const id = randomId();
    store?.setItem(KEY, id);
    return id;
  } catch {
    return randomId();
  }
}

function randomId(): string {
  try {
    const c = (globalThis as unknown as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c?.randomUUID) return c.randomUUID().replace(/-/g, '');
  } catch { /* ignore */ }
  return Math.random().toString(36).slice(2) + now().toString(36);
}

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const DIGITS_RE = /\d{6,}/g;
const JWT_RE = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
const BEARER_RE = /(?:bearer\s+)[A-Za-z0-9._-]+/gi;
const LONG_OPAQUE_RE = /\b[A-Za-z0-9+/=_-]{32,}\b/g;
const URL_ORIGIN_RE = /https?:\/\/[^/\s]+/g;
const QUERY_RE = /[?#][^\s)]*/g;

/** Strip HTML/control chars, redact PII + secrets, cap length. */
export function sanitizeText(input: string | undefined | null, maxLength: number): string {
  if (!input) return '';
  let s = String(input).replace(/<[^>]*>/g, '');
  // eslint-disable-next-line no-control-regex
  s = s.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, ' ');
  s = s.replace(JWT_RE, '[TOKEN_REDACTED]');
  s = s.replace(BEARER_RE, 'Bearer [TOKEN_REDACTED]');
  s = s.replace(LONG_OPAQUE_RE, '[TOKEN_REDACTED]');
  s = s.replace(EMAIL_RE, '[EMAIL_REDACTED]');
  s = s.replace(DIGITS_RE, '[NUM_REDACTED]');
  s = s.replace(/\s+/g, ' ').trim();
  if (s.length > maxLength) s = s.slice(0, maxLength);
  return s;
}

/**
 * Keep the first N frames, drop URL origins + query strings, redact PII, cap length.
 * Prefers frames from our own bundle when the origin is identifiable; otherwise keeps
 * the first frames as-is (minified builds have no reliable "our code" marker).
 */
export function sanitizeStack(stack: string | undefined | null): string {
  if (!stack) return '';
  const lines = String(stack)
    .replace(/\r/g, '')
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const framesOnly = lines.filter((l) => /^at\s|@|\.js|\.ts|\.tsx/.test(l));
  const chosen = (framesOnly.length > 0 ? framesOnly : lines).slice(0, MAX_STACK_FRAMES);

  const cleaned = chosen.map((l) => {
    let s = l.replace(/<[^>]*>/g, '');
    s = s.replace(URL_ORIGIN_RE, '');
    s = s.replace(QUERY_RE, '');
    s = s.replace(JWT_RE, '[TOKEN_REDACTED]');
    s = s.replace(BEARER_RE, 'Bearer [TOKEN_REDACTED]');
    s = s.replace(EMAIL_RE, '[EMAIL_REDACTED]');
    s = s.replace(/\s+/g, ' ').trim();
    return s;
  });

  let joined = cleaned.join('\n');
  if (joined.length > MAX_STACK_LEN) joined = joined.slice(0, MAX_STACK_LEN);
  return joined;
}

function signatureOf(errorName: string, stackTop: string): string {
  const firstFrame = stackTop.split('\n')[0] || '';
  return `${errorName}|${firstFrame}`;
}

/** Rate-limit + de-dupe gate. Returns true if this event may be sent. */
function allowSend(signature: string, at: number): boolean {
  sentTimestamps = sentTimestamps.filter((t) => at - t < WINDOW_MS);
  if (sentTimestamps.length >= MAX_PER_WINDOW) return false;

  const last = recentSignatures.get(signature);
  if (last !== undefined && at - last < DEDUPE_MS) return false;

  // prune old signatures opportunistically
  if (recentSignatures.size > 50) {
    for (const [k, v] of recentSignatures) if (at - v > DEDUPE_MS) recentSignatures.delete(k);
  }
  recentSignatures.set(signature, at);
  sentTimestamps.push(at);
  return true;
}

/** Build a fully-sanitized event from a raw error. Exposed for tests. */
export function buildEvent(
  eventType: DiagnosticEventType,
  errorName: string,
  errorMessage: string,
  stack: string,
  routeOverride?: string,
): DiagnosticEvent {
  return {
    eventType,
    platform: detectPlatform(),
    appVersion: APP_VERSION,
    route: routeOverride ?? currentRoute(),
    errorName: sanitizeText(errorName, MAX_NAME_LEN),
    errorMessage: sanitizeText(errorMessage, MAX_MESSAGE_LEN),
    stackTop: sanitizeStack(stack),
    sessionId: sessionId(),
    childAgeBand: currentAgeBand(),
    timestampMs: now(),
  };
}

/** Fire-and-forget POST. Never throws; uses keepalive so it survives an unload/crash. */
function post(event: DiagnosticEvent): void {
  try {
    const body = JSON.stringify(event);
    const url = ingestUrl();
    const f = (globalThis as unknown as { fetch?: typeof fetch }).fetch;
    if (typeof f === 'function') {
      void f(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => { /* diagnostics are best-effort */ });
      return;
    }
    // Last resort for unload paths where fetch is gone.
    const nav = (globalThis as unknown as { navigator?: { sendBeacon?: (u: string, d: unknown) => boolean } }).navigator;
    nav?.sendBeacon?.(url, body);
  } catch { /* never let telemetry break the app */ }
}

/** Report a sanitized diagnostic, honoring the client rate-limit/dedupe. */
export function reportDiagnostic(input: {
  eventType: DiagnosticEventType;
  errorName: string;
  errorMessage: string;
  stack?: string;
  route?: string;
}): void {
  try {
    const event = buildEvent(input.eventType, input.errorName, input.errorMessage, input.stack ?? '', input.route);
    const sig = signatureOf(event.errorName, event.stackTop);
    if (!allowSend(sig, now())) return;
    post(event);
  } catch { /* ignore */ }
}

/** Called by React ErrorBoundary.componentDidCatch — a render crash. */
export function reportReactError(error: unknown, componentStack?: string): void {
  const err = error as { name?: string; message?: string; stack?: string } | null;
  const stack = (err?.stack ? err.stack + '\n' : '') + (componentStack ?? '');
  reportDiagnostic({
    eventType: 'crash',
    errorName: err?.name || 'Error',
    errorMessage: err?.message || String(error),
    stack,
  });
}

/** Wire window.onerror + unhandledrejection once. Safe to call on every module load. */
export function installGlobalDiagnostics(): void {
  if (globalHandlersInstalled) return;
  const w = (globalThis as unknown as {
    addEventListener?: (t: string, cb: (e: unknown) => void) => void;
  });
  if (typeof w.addEventListener !== 'function') return;
  globalHandlersInstalled = true;

  w.addEventListener('error', (e: unknown) => {
    const ev = e as { error?: { name?: string; message?: string; stack?: string }; message?: string; filename?: string };
    // Ignore resource-load errors (img/script) which have no `.error` and no message.
    if (!ev?.error && !ev?.message) return;
    reportDiagnostic({
      eventType: 'error',
      errorName: ev.error?.name || 'Error',
      errorMessage: ev.error?.message || ev.message || 'Uncaught error',
      stack: ev.error?.stack || '',
    });
  });

  w.addEventListener('unhandledrejection', (e: unknown) => {
    const ev = e as { reason?: { name?: string; message?: string; stack?: string } | string };
    const reason = ev?.reason;
    const asObj = (typeof reason === 'object' ? reason : null) as { name?: string; message?: string; stack?: string } | null;
    reportDiagnostic({
      eventType: 'error',
      errorName: asObj?.name || 'UnhandledRejection',
      errorMessage: asObj?.message || (typeof reason === 'string' ? reason : 'Unhandled promise rejection'),
      stack: asObj?.stack || '',
    });
  });
}

/** Test hook — reset rate-limit + dedupe state between cases. */
export function __resetDiagnosticsForTests(): void {
  sentTimestamps = [];
  recentSignatures.clear();
  globalHandlersInstalled = false;
}
